import { ColorEnum } from '@/constants/color'
import { PriceValue } from '@/features/shiftCloseouts/types'
import Typography from '@/features/ui/typography'
import { formatPrice } from '@/utils/priceHelper'
import { ComponentType, FC } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, View } from 'react-native'
import { SvgProps } from 'react-native-svg'

interface PaymentMethodInfoProps {
    automatic: boolean
    expectedAmount: PriceValue
    Icon?: ComponentType<SvgProps>
    label: string
}

const PaymentMethodInfo: FC<PaymentMethodInfoProps> = ({
    automatic,
    expectedAmount,
    Icon,
    label,
}) => {
    const { t } = useTranslation()

    return (
        <View style={styles.container}>
            {Icon && (
                <View style={[styles.icon, automatic && styles.automaticIcon]}>
                    <Icon
                        fill={automatic ? ColorEnum.GRAY_400 : ColorEnum.BRICK_500}
                        height={24}
                        width={24}
                    />
                </View>
            )}
            <View style={styles.text}>
                <View style={styles.labelRow}>
                    <Typography.Description
                        font="bold"
                        numberOfLines={1}
                        size="large"
                        style={styles.label}
                    >
                        {label}
                    </Typography.Description>
                    {automatic && (
                        <View style={styles.automaticBadge}>
                            <Typography.Description
                                font="bold"
                                size="tiny"
                                style={styles.automaticText}
                            >
                                {t('shiftCloseouts.paymentMethods.automatic')}
                            </Typography.Description>
                        </View>
                    )}
                </View>
                <Typography.Description font="medium" size="tiny" style={styles.expected}>
                    {t('shiftCloseouts.paymentMethods.expected', {
                        amount: formatPrice(expectedAmount.amount, expectedAmount.currency),
                    })}
                </Typography.Description>
            </View>
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        minWidth: 0,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    icon: {
        height: 40,
        width: 40,
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
        backgroundColor: ColorEnum.Brick50,
    },
    automaticIcon: {
        backgroundColor: ColorEnum.GRAY_100,
    },
    text: {
        flex: 1,
        minWidth: 0,
        gap: 2,
    },
    labelRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    label: {
        flexShrink: 1,
        color: ColorEnum.GRAY_900,
    },
    automaticBadge: {
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 4,
        backgroundColor: ColorEnum.VIOLET_100,
    },
    automaticText: {
        color: ColorEnum.GRAY_600,
    },
    expected: {
        color: ColorEnum.GRAY_600,
    },
})

export default PaymentMethodInfo
